import { z } from "zod";

// §9.17 — Studio-Effekt-Stack (komponierbarer Editor). Eine Effekt-INSTANZ (id + enabled + Typ +
// Parameter) ist reine PROVENANCE: der Save-Preis hängt NICHT an den Effekten (reine Pixel-Arbeit,
// läuft gratis im Browser-Pyodide), sondern an den `steps` (edit.ts, STUDIO_STEP_ORDER). `chroma-key`
// ist der EINZIGE Effekt mit Preis-Wirkung — er stiftet die Schritte `chroma-key` + `transparency-crop`
// (die Ableitung lebt in studioPlan.deriveSteps). Die Reihenfolge im Stack = die WORKER-Ausführungs-
// reihenfolge; die PREIS-`steps` bleiben kanonisch sortiert (entkoppelt, vgl. edit.ts).

/** RGB-Farbe 0..255 (Tint/Outline/Glow/Schatten). */
export const Rgb = z.tuple([
  z.number().int().min(0).max(255),
  z.number().int().min(0).max(255),
  z.number().int().min(0).max(255),
]);
export type Rgb = z.infer<typeof Rgb>;

const base = { id: z.string().min(1), enabled: z.boolean() };

/**
 * Ein Studio-Effekt als diskriminierte Union (`type`). Bounds spiegeln die Klemmung im Worker
 * (browser-ops/worker.ts) und in ops.py — die Schemas sind die SSOT-Grenzen (D12). Provenance/
 * UI-Übergabe; KEINE Preis-Relevanz (s. o.).
 */
export const StudioEffect = z.discriminatedUnion("type", [
  z.object({ ...base, type: z.literal("speed"), factor: z.number().min(0.1).max(10) }),
  z.object({
    ...base,
    type: z.literal("color"),
    brightness: z.number().min(-100).max(100),
    contrast: z.number().min(-100).max(100),
    saturation: z.number().min(-100).max(100),
    hue: z.number().min(-180).max(180),
    tint: Rgb.nullable(),
    tintStrength: z.number().min(0).max(100),
  }),
  z.object({
    ...base,
    type: z.literal("transform"),
    flipH: z.boolean(),
    flipV: z.boolean(),
    rotateQuarters: z.number().int().min(0).max(3),
    scale: z.number().min(10).max(400),
  }),
  z.object({
    ...base,
    type: z.literal("chroma-key"),
    keyColor: z.enum(["auto", "green", "blue"]),
    tolerance: z.number().min(0).max(100),
  }),
  z.object({ ...base, type: z.literal("outline"), thickness: z.number().int().min(1).max(16), color: Rgb }),
  z.object({
    ...base,
    type: z.literal("glow"),
    radius: z.number().int().min(1).max(64),
    color: Rgb,
    strength: z.number().min(0).max(100),
  }),
  z.object({
    ...base,
    type: z.literal("pixelate"),
    cell: z.number().int().min(1).max(64),
    colors: z.number().int().min(0).max(64), // 0|1 ⇒ keine Quantisierung
    upscale: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal("drop-shadow"),
    dx: z.number().int().min(-256).max(256),
    dy: z.number().int().min(-256).max(256),
    blur: z.number().int().min(0).max(32),
    color: Rgb,
    opacity: z.number().min(0).max(100),
  }),
]);
export type StudioEffect = z.infer<typeof StudioEffect>;
export type StudioEffectType = StudioEffect["type"];

/** Alle Effekt-Typen in der „+ Effekt"-Reihenfolge (UI-Menü). */
export const STUDIO_EFFECT_TYPES = [
  "speed",
  "color",
  "transform",
  "chroma-key",
  "outline",
  "glow",
  "pixelate",
  "drop-shadow",
] as const satisfies readonly StudioEffectType[];

/** Effekt-Typen, die der WebM-Export-Job (v1) server-seitig NICHT nachbildet (nur Schnitt +
 *  chroma-key laufen dort). Ist ein anderer aktiver Effekt im Stack, wird der WebM-Export
 *  gesperrt (statt ihn still zu verschlucken) — Rest §13. */
export const WEBM_UNSUPPORTED_EFFECT_TYPES: ReadonlySet<StudioEffectType> = new Set([
  "speed",
  "color",
  "transform",
  "outline",
  "glow",
  "pixelate",
  "drop-shadow",
]);
